import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  Divider,
} from '@mui/material';
import {
  Close,
  Medication,
  MedicalServices,
  Build,
  Calculate,
  Add,
  Check,
} from '@mui/icons-material';
import {
  Treatment,
  TreatmentType,
  getTreatmentDisplayName,
  getTreatmentTypeLabel,
} from '../../types/treatment.types';

interface TreatmentInfoData {
  treatment: Treatment;
  diseaseName: string;
  protocolName: string;
}

interface CaseTreatmentInfoDialogProps {
  open: boolean;
  onClose: () => void;
  treatmentInfo: TreatmentInfoData | null;
  onCalculateDosage?: (treatment: Treatment) => void;
  onAddToSelected?: () => void;
  isSelected?: boolean;
}

const CaseTreatmentInfoDialog: React.FC<CaseTreatmentInfoDialogProps> = ({
  open,
  onClose,
  treatmentInfo,
  onCalculateDosage,
  onAddToSelected,
  isSelected = false,
}) => {
  if (!treatmentInfo) {
    return null;
  }

  const { treatment, diseaseName, protocolName } = treatmentInfo;
  const treatmentName = getTreatmentDisplayName(treatment);

  // Get treatment type icon
  const getTreatmentTypeIcon = (type: TreatmentType) => {
    switch (type) {
      case TreatmentType.Drug:
        return <Medication sx={{ color: 'info.main' }} />;
      case TreatmentType.Procedure:
        return <MedicalServices sx={{ color: 'warning.main' }} />;
      case TreatmentType.OtherTreatment:
        return <Build sx={{ color: 'secondary.main' }} />;
      default:
        return <Medication sx={{ color: 'info.main' }} />;
    }
  };

  const handleCalculateDosage = () => {
    if (onCalculateDosage) {
      onCalculateDosage(treatment);
      onClose();
    }
  };

  const handleAddToSelected = () => {
    if (onAddToSelected) {
      onAddToSelected();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {getTreatmentTypeIcon(treatment.type)}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Treatment Details
          </Typography>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Treatment Name */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            {treatmentName}
          </Typography>

          {/* Type and Protocol Chips */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              icon={getTreatmentTypeIcon(treatment.type)}
              label={getTreatmentTypeLabel(treatment.type)}
              color="info"
              variant="outlined"
            />
            {treatment.treatment_optionality && (
              <Chip
                label={treatment.treatment_optionality.name}
                variant="outlined"
              />
            )}
          </Box>

          {/* Context info */}
          <Typography variant="body2" color="text.secondary">
            For diagnosis: <strong>{diseaseName}</strong>
          </Typography>
          {protocolName && (
            <Typography variant="body2" color="text.secondary">
              Protocol: <strong>{protocolName}</strong>
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Treatment Information */}
        {treatment.info && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Information
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {treatment.info}
            </Typography>
          </Box>
        )}

        {/* Follow Up Information */}
        {treatment.follow_up && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Follow Up
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {treatment.follow_up}
            </Typography>
          </Box>
        )}

        {/* Drug-specific information */}
        {treatment.type === TreatmentType.Drug && treatment.drug_substance && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Drug Information
            </Typography>
            <Typography variant="body2">
              <strong>Substance:</strong> {treatment.drug_substance.name}
            </Typography>
          </Box>
        )}

        {/* Procedure-specific information */}
        {treatment.type === TreatmentType.Procedure && treatment.procedure && (
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="subtitle1"
              color="primary"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              Procedure Information
            </Typography>
            <Typography variant="body2">
              <strong>Procedure:</strong> {treatment.procedure.name}
            </Typography>
            {treatment.procedure.procedure_type && (
              <Typography variant="body2">
                <strong>Type:</strong> {treatment.procedure.procedure_type.name}
              </Typography>
            )}
          </Box>
        )}

        {/* No additional info */}
        {!treatment.info && !treatment.follow_up && (
          <Typography variant="body2" color="text.secondary">
            No additional information available for this treatment.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        {treatment.type === TreatmentType.Drug &&
          treatment.drug_substance?.has_doses !== false &&
          onCalculateDosage && (
            <Button
              onClick={handleCalculateDosage}
              variant="outlined"
              startIcon={<Calculate />}
              color="secondary"
            >
              Calculate Dosage
            </Button>
          )}
        {onAddToSelected && (
          <Button
            onClick={handleAddToSelected}
            variant="contained"
            startIcon={isSelected ? <Check /> : <Add />}
            color={isSelected ? 'success' : 'primary'}
            disabled={isSelected}
          >
            {isSelected ? 'Already Selected' : 'Add to Selected'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CaseTreatmentInfoDialog;
